import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import {
  ArchiveIcon,
  ArchiveRestore,
  CircleDollarSign,
  CloudCog,
  Info,
  RefreshCw,
  Scale,
  Timer,
  Trash2,
  UserMinus,
  UserPlus,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import { ArchivedEntityAlert } from '@/components/archived-entity-alert';
import { ScheduledArchivationAlert } from '@/components/scheduled-archivation-alert';
import { StatusBadgeType, usePermissions } from '@/modules/common';
import { getMemberStatusText } from '@/modules/common/components/molecules/badge/status-configs/team-member-status-config';
import { EditChangeAlert } from '@/modules/common/components/molecules/edit-change-alert';
import { ScheduledChangeAlert } from '@/modules/common/components/molecules/scheduled-change-alert';
import {
  ActionButtonConfig,
  FeatureDetailsTemplate,
  FeatureMode,
  FeatureTab,
  useTabValidation,
} from '@/modules/common/components/templates';
import { useFeatureFlags, useOrganizationDefaults } from '@/modules/common/hooks';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { PatchEntityResult } from '@/modules/common/types/patch-entity-result';
import { NeedsReviewMemberAlert } from '@/modules/team/components/member-details/needs-review-member-alert';
import {
  ArchiveTeamMemberModal,
  DeleteTeamMembersModal,
  InviteCooldownModal,
  InviteTeamMemberModal,
  ResendInviteTeamMemberModal,
  RevokeAccessTeamMemberModal,
  UnarchiveTeamMemberModal,
} from '@/modules/team/components/modals';
import { useTeamMemberDetails, useTeamMemberDraft } from '@/modules/team/hooks';
import { useInviteCooldown } from '@/modules/team/hooks/use-invite-cooldown';
import { usePatchTeamMemberChange } from '@/modules/team/hooks/use-patch-team-member-change';
import { useTeamMemberByRevisionDate } from '@/modules/team/hooks/use-team-member-by-revision-date';
import { useTeamMemberChanges } from '@/modules/team/hooks/use-team-member-changes';
import { mapTeamMemberIntegrationsToRawResponse } from '@/modules/team/mappers/team-member-integration.mapper';
import {
  TeamMemberBillingTab,
  TeamMemberBillingTabRef,
  TeamMemberCapacityTab,
  TeamMemberCapacityTabRef,
  TeamMemberChangesTab,
  TeamMemberInfoTab,
  TeamMemberInfoTabRef,
  TeamMemberIntegrationsTab,
  TeamMemberIntegrationsTabRef,
  TeamMemberTimeOffTab,
} from '@/modules/team/pages/details/tab-pages';
import { MemberStatus, TeamMember, TeamMemberFormData, TeamMemberTab } from '@/modules/team/types';
import { TeamMemberChangeType } from '@/modules/team/types/team-member-changes';
import { teamMemberBillingSchema } from '@/modules/team/validations/team-member-billing-schema';
import { teamMemberCapacitySchema } from '@/modules/team/validations/team-member-capacity-schema';
import { teamMemberInfoSchema } from '@/modules/team/validations/team-member-info-schema';
import { AuthenticatedRoutes } from '@/router/routes';
import {
  formatDateToNonISOFormat,
  formatDisplayDate,
  getTodayISOString,
} from '@/utils/date-formatters';

/**
 * Comprehensive team member management page supporting create, edit, and read modes.
 * Uses the enhanced FeatureDetailsTemplate for streamlined form management.
 *
 * Features:
 * - Multi-mode operation (Create/Edit/Read) with role-based access control
 * - Tab-based form navigation with draft data persistence
 * - Unsaved changes detection and confirmation dialogs
 * - Team member archiving/unarchiving and deletion capabilities
 * - Responsive design with mobile-optimized interactions
 * - Avatar upload with optimistic UI updates
 * - Streamlined form validation and state management via template
 */
export function TeamMemberDetailsPage() {
  const { t } = useTranslation(['authenticated.team', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const { memberId, changeId } = useParams<{ memberId: string; changeId?: string }>();
  const queryClient = useQueryClient();

  // Determine if in EditChange mode
  const isEditChangeMode = !!changeId;

  // Redirect back-button navigation away from Edit Change mode.
  // Entry navigation passes a `directNavigation` state marker, which is cleared on mount.
  // If the marker is absent (back/forward button), redirect to the Changes tab.
  const hasProcessedEditChangeEntry = useRef(false);
  useEffect(() => {
    if (!isEditChangeMode || hasProcessedEditChangeEntry.current) return;
    hasProcessedEditChangeEntry.current = true;

    if (location.state?.directNavigation) {
      // Clear the marker so back-button navigation to this entry won't have it
      navigate(location.pathname + location.search, { replace: true, state: null });
    } else {
      // No marker — arrived via back/forward button, redirect to Changes tab
      navigate(`/team/${memberId}?tab=${TeamMemberTab.Changes}`, { replace: true });
    }
  }, [isEditChangeMode, location.state, location.pathname, location.search, memberId, navigate]);

  // Tab refs for form management
  const teamMemberInfoFormRef = useRef<TeamMemberInfoTabRef>(null);
  const teamMemberCapacityFormRef = useRef<TeamMemberCapacityTabRef>(null);
  const teamMemberBillingFormRef = useRef<TeamMemberBillingTabRef>(null);
  const teamMemberIntegrationsFormRef = useRef<TeamMemberIntegrationsTabRef>(null);

  // Ref to hold allowNavigation function from unsaved changes guard
  // Used to bypass guard after successful save operations in EditChange mode
  const allowNavigationRef = useRef<(() => void) | null>(null);

  // Modal state
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isResendInviteModalOpen, setIsResendInviteModalOpen] = useState(false);
  const [isRevokeAccessModalOpen, setIsRevokeAccessModalOpen] = useState(false);
  const [isInviteCooldownModalOpen, setIsInviteCooldownModalOpen] = useState(false);

  // Cognito enforces a 1-hour cooldown between (re)sending invites for the same user.
  // Track the last send client-side in localStorage and gate the modal opens below.
  // Backend-driven enforcement is planned — when available this can be replaced.
  const { remainingMs, isOnCooldown, recordSend: recordInviteSend } = useInviteCooldown(memberId);

  const handleOpenInviteModal = useCallback(() => {
    if (isOnCooldown) {
      setIsInviteCooldownModalOpen(true);
      return;
    }
    setIsInviteModalOpen(true);
  }, [isOnCooldown]);

  const handleOpenResendInviteModal = useCallback(() => {
    if (isOnCooldown) {
      setIsInviteCooldownModalOpen(true);
      return;
    }
    setIsResendInviteModalOpen(true);
  }, [isOnCooldown]);

  // Form state change handler for the template
  const [formStateChangeHandler, setFormStateChangeHandler] = useState<
    | ((
        isDirty: boolean,
        hasChanges?: boolean,
        isValid?: boolean,
        hasOnlyNonVersionedChanges?: boolean
      ) => void)
    | null
  >(null);

  // Track whether only non-versioned fields changed (for disabling date picker)
  const [hasOnlyNonVersionedChanges, setHasOnlyNonVersionedChanges] = useState(false);

  const {
    teamMemberData,
    mode: originalMode,
    currentTab: originalCurrentTab,
    isLoading,
    isLoadingTeamMember,
    isUploadingAvatar,
    error,
    handleSaveTeamMember,
    handleTabChange,
    handleAvatarUpload,
    handleAvatarRemove,
  } = useTeamMemberDetails();

  // Fetch team member changes — used for EditChange metadata and scheduled change alerts on tabs.
  // Always enabled so the changes tab can reuse the cached data (same query key with empty filters).
  const { data: changesData } = useTeamMemberChanges({
    teamMemberId: Number(memberId) || 0,
    filters: {},
    enabled: !!memberId && memberId !== 'new',
  });

  const changeToEdit = useMemo(() => {
    if (!isEditChangeMode || !changesData?.items) return undefined;
    return changesData.items.find(c => c.id === Number(changeId));
  }, [isEditChangeMode, changesData, changeId]);

  // Derive next scheduled change per type from already-fetched changes data
  const nextChangeByType = useMemo(() => {
    if (!changesData?.items?.length || isEditChangeMode) return {};

    const todayStr = getTodayISOString().slice(0, 10);

    const result: Partial<
      Record<TeamMemberChangeType, { type: TeamMemberChangeType; startDate: string; id: number }>
    > = {};

    for (const change of changesData.items) {
      const dateStr = change.startDate.slice(0, 10);
      if (dateStr > todayStr) {
        const existing = result[change.type];
        if (!existing || dateStr < existing.startDate.slice(0, 10)) {
          result[change.type] = {
            type: change.type,
            startDate: change.startDate,
            id: change.id,
          };
        }
      }
    }

    return result;
  }, [changesData, isEditChangeMode]);

  // Fetch full team member state at the change's revision date (EditChange mode)
  // Uses GET /api/team/members/one/full?id=X&revisionDate=Y which returns the complete state
  // Integration changes are non-versioned, so no historical fetch is needed
  const shouldFetchHistorical =
    isEditChangeMode && !!changeToEdit && changeToEdit.type !== TeamMemberChangeType.Integration;

  const { data: historicalTeamMemberData, isLoading: isLoadingHistorical } =
    useTeamMemberByRevisionDate({
      params: {
        teamMemberId: Number(memberId),
        revisionDate: changeToEdit?.startDate || '',
      },
      enabled: shouldFetchHistorical && !!changeToEdit?.startDate,
    });

  // Mutation for patching a team member change (EditChange mode)
  const patchChangeMutation = usePatchTeamMemberChange({
    onSuccess: () => {
      allowNavigationRef.current?.();
      navigate(`/team/${memberId}?tab=${TeamMemberTab.Changes}`);
    },
  });

  // Override mode and currentTab when in EditChange mode
  const mode = isEditChangeMode ? FeatureMode.EditChange : originalMode;
  const currentTab = useMemo(() => {
    if (isEditChangeMode && changeToEdit) {
      switch (changeToEdit.type) {
        case TeamMemberChangeType.Info:
          return TeamMemberTab.Info;
        case TeamMemberChangeType.Billing:
          return TeamMemberTab.Billing;
        case TeamMemberChangeType.Capacity:
          return TeamMemberTab.Capacity;
        case TeamMemberChangeType.Integration:
          return TeamMemberTab.Integrations;
        default:
          return originalCurrentTab;
      }
    }
    return originalCurrentTab;
  }, [isEditChangeMode, changeToEdit, originalCurrentTab]);

  // Build effective team member data for EditChange mode
  // The revision date endpoint returns a full TeamMember at that point in time,
  // so no manual field merging is needed
  const effectiveTeamMemberData: TeamMember | undefined = useMemo(() => {
    if (!isEditChangeMode || !changeToEdit || !teamMemberData) {
      return teamMemberData;
    }

    // Integration changes are non-versioned, use current data
    if (changeToEdit.type === TeamMemberChangeType.Integration) {
      return teamMemberData;
    }

    // Show loading state while fetching historical data
    if (isLoadingHistorical) return undefined;

    // If no historical data returned, fall back to current data
    // This can happen when the change is currently active
    if (!historicalTeamMemberData) return teamMemberData;

    // Use the historical snapshot directly - it's already a full TeamMember
    return historicalTeamMemberData;
  }, [
    isEditChangeMode,
    changeToEdit,
    teamMemberData,
    historicalTeamMemberData,
    isLoadingHistorical,
  ]);

  // Determine disabled tabs for EditChange mode
  const disabledTabs = useMemo(() => {
    if (!isEditChangeMode || !changeToEdit) return [];

    const allTabs = [
      TeamMemberTab.Info,
      TeamMemberTab.Billing,
      TeamMemberTab.Capacity,
      TeamMemberTab.Integrations,
      TeamMemberTab.Changes,
    ];

    return allTabs.filter(tab => tab !== currentTab);
  }, [isEditChangeMode, changeToEdit, currentTab]);

  // Cancel handler for EditChange mode
  const handleCancelEditChange = useCallback(() => {
    navigate(`/team/${memberId}?tab=${TeamMemberTab.Changes}`, { replace: true });
  }, [navigate, memberId]);

  // Handler to refetch team member data
  const handleRefetch = () => {
    queryClient.invalidateQueries({ queryKey: ['team-member', memberId] });
  };

  // Get allowNavigation function from template's unsaved changes guard
  const handleAllowNavigationReady = useCallback((allowNavigation: () => void) => {
    allowNavigationRef.current = allowNavigation;
  }, []);

  // Get wrapped tab change handler from template
  const [wrappedTabChangeHandler, setWrappedTabChangeHandler] = useState<
    ((tab: string, additionalParams?: Record<string, string>) => void) | null
  >(null);

  const handleTabChangeHandlerReady = useCallback(
    (handler: (tab: string, additionalParams?: Record<string, string>) => void) => {
      setWrappedTabChangeHandler(() => handler);
    },
    []
  );

  // Fetch organization defaults to use as fallback for billing fields when billing tab is never opened
  const { data: organizationDefaults } = useOrganizationDefaults();
  const defaultCurrency = organizationDefaults?.currency.code;

  // Draft management
  const {
    saveDraftData,
    getDraftData,
    clearDraft,
    hasDraftData,
    getFinalSubmissionData: getFinalSubmissionDataRaw,
  } = useTeamMemberDraft();

  // Wrap getFinalSubmissionData to inject the organization's default currency as fallback
  const getFinalSubmissionData = useCallback(
    () => getFinalSubmissionDataRaw(defaultCurrency),
    [getFinalSubmissionDataRaw, defaultCurrency]
  );

  // Get current tab draft data
  const currentTabDraftData = getDraftData(currentTab);

  // Get form state change handler from template
  // Wraps the template's handler to also track hasOnlyNonVersionedChanges
  const handleFormStateChangeHandlerReady = useCallback(
    (handler: (isDirty: boolean, hasChanges?: boolean, isValid?: boolean) => void) => {
      setFormStateChangeHandler(
        () =>
          (
            isDirty: boolean,
            hasChanges?: boolean,
            isValid?: boolean,
            onlyNonVersioned?: boolean
          ) => {
            // Call the original template handler with all form state params
            handler(isDirty, hasChanges, isValid);
            // Also update our local state for non-versioned changes tracking
            setHasOnlyNonVersionedChanges(onlyNonVersioned ?? false);
          }
      );
    },
    []
  );

  // Mapping from tab to change type for scheduled change alerts
  const TAB_TO_CHANGE_TYPE: Partial<Record<TeamMemberTab, TeamMemberChangeType>> = useMemo(
    () => ({
      [TeamMemberTab.Info]: TeamMemberChangeType.Info,
      [TeamMemberTab.Billing]: TeamMemberChangeType.Billing,
      [TeamMemberTab.Capacity]: TeamMemberChangeType.Capacity,
    }),
    []
  );

  // Handler to navigate to Changes tab with pre-applied filters for a scheduled change
  const handleShowScheduledChange = useCallback(
    (changeType: TeamMemberChangeType, startDate: string) => {
      const formattedDate = formatDisplayDate(startDate);
      const dateRangeParam = `${formattedDate}:${formattedDate}`;

      const tabChangeHandler = wrappedTabChangeHandler || handleTabChange;
      tabChangeHandler(TeamMemberTab.Changes, {
        changeType,
        dateRange: dateRangeParam,
      });
    },
    [wrappedTabChangeHandler, handleTabChange]
  );

  // Helper function to wrap tab content with archived alert and edit change alert when needed
  const wrapWithAlert = useCallback(
    (content: React.ReactNode, tab: TeamMemberTab) => {
      const alerts: React.ReactNode[] = [];

      if (effectiveTeamMemberData?.status === MemberStatus.NeedsReview) {
        alerts.push(<NeedsReviewMemberAlert key="needsReview" />);
      }

      // Add archived alert if team member is archived
      if (effectiveTeamMemberData?.status === MemberStatus.Archived) {
        alerts.push(
          <ArchivedEntityAlert
            key="archived"
            message={t('authenticated.team:details.archivedTeamMemberMessage')}
          />
        );
      }

      // Add scheduled archivation alert if team member is scheduled to be archived
      if (!isEditChangeMode && effectiveTeamMemberData?.nextArchiveDate) {
        alerts.push(
          <ScheduledArchivationAlert
            key="scheduledArchive"
            message={t('authenticated.team:scheduledArchive.willBeArchivedOn', {
              date: formatDisplayDate(effectiveTeamMemberData.nextArchiveDate),
            })}
            className="mb-4"
          />
        );
      }

      // Add scheduled unarchivation alert if team member is scheduled to be unarchived
      if (!isEditChangeMode && effectiveTeamMemberData?.nextUnarchiveDate) {
        alerts.push(
          <ScheduledArchivationAlert
            key="scheduledUnarchive"
            message={t('authenticated.team:scheduledArchive.willBeUnarchivedOn', {
              date: formatDisplayDate(effectiveTeamMemberData.nextUnarchiveDate),
            })}
            className="mb-4"
          />
        );
      }

      // Add edit change alert if in EditChange mode on relevant tabs
      if (isEditChangeMode && tab !== TeamMemberTab.Changes) {
        alerts.push(
          <EditChangeAlert
            key="editChange"
            message={t('authenticated.team:details.tabs.changes.editChangeMessage')}
          />
        );
      }

      // Add scheduled change alert if there's a future change for this tab's type.
      // Skip if the next change is the scheduled archivation/unarchivation — those are
      // already surfaced by ScheduledArchivationAlert above.
      if (!isEditChangeMode) {
        const changeType = TAB_TO_CHANGE_TYPE[tab];
        if (changeType) {
          const nextChange = nextChangeByType[changeType];
          const isArchivationChange =
            nextChange?.startDate &&
            (nextChange.startDate.slice(0, 10) ===
              effectiveTeamMemberData?.nextArchiveDate?.slice(0, 10) ||
              nextChange.startDate.slice(0, 10) ===
                effectiveTeamMemberData?.nextUnarchiveDate?.slice(0, 10));
          if (nextChange && !isArchivationChange) {
            alerts.push(
              <ScheduledChangeAlert
                key="scheduledChange"
                date={formatDisplayDate(nextChange.startDate)}
                onShowChange={() =>
                  handleShowScheduledChange(nextChange.type, nextChange.startDate)
                }
              />
            );
          }
        }
      }

      if (alerts.length > 0) {
        if (tab === TeamMemberTab.TimeOff) {
          return (
            <div className="flex h-full min-h-0 flex-col gap-4">
              {alerts}
              <div className="min-h-0 flex-1">{content}</div>
            </div>
          );
        }

        return (
          <div className="space-y-4">
            {alerts}
            {content}
          </div>
        );
      }

      return content;
    },
    [
      effectiveTeamMemberData?.status,
      effectiveTeamMemberData?.nextArchiveDate,
      effectiveTeamMemberData?.nextUnarchiveDate,
      isEditChangeMode,
      t,
      TAB_TO_CHANGE_TYPE,
      nextChangeByType,
      handleShowScheduledChange,
    ]
  );

  const hasInfoTabWarning = Boolean(
    effectiveTeamMemberData?.manager?.isDeleted ||
      effectiveTeamMemberData?.department?.isDeleted ||
      effectiveTeamMemberData?.seniority?.isDeleted ||
      effectiveTeamMemberData?.role?.isDeleted
  );

  // check is all integrations connected
  const hasIntegrationsTabWarning = Boolean(
    (mode === FeatureMode.Edit || mode === FeatureMode.EditChange) &&
      effectiveTeamMemberData?.integrations.some(integration => !integration.selectedValue?.id)
  );

  // Tab validation state management
  const { tabValid, handleSetTabValid } = useTabValidation([
    TeamMemberTab.Info,
    TeamMemberTab.Capacity,
    TeamMemberTab.Billing,
    TeamMemberTab.Integrations,
    TeamMemberTab.Changes,
  ]);

  const [patchResultToDisplay, setPatchResultToDisplay] = useState<PatchEntityResult | null>(null);

  const handleDirectSave = useCallback(
    async (
      data: TeamMemberFormData,
      effectiveStartDate?: Date,
      effectiveEndDate?: Date | null
    ): Promise<void> => {
      // In EditChange mode, use patch change mutation to update the existing change
      if (isEditChangeMode && changeToEdit) {
        const startDate = effectiveStartDate || new Date(changeToEdit.startDate);

        let endDate: Date | null = null;
        if (effectiveEndDate !== undefined) {
          endDate = effectiveEndDate;
        } else if (changeToEdit.endDate) {
          endDate = new Date(changeToEdit.endDate);
        }

        return new Promise((resolve, reject) => {
          patchChangeMutation.mutate(
            {
              stateDeltaId: Number(changeId),
              teamMemberId: Number(memberId),
              data,
              effectiveStartDate: startDate,
              effectiveEndDate: endDate,
              changeType: changeToEdit.type,
            },
            {
              onSuccess: () => resolve(),
              onError: error => reject(error),
            }
          );
        });
      }

      // Regular mode (not EditChange) - use normal team member save flow
      return new Promise((resolve, reject) => {
        // Format dates to YYYY-MM-DD format for API
        const formattedStartDate = effectiveStartDate
          ? formatDateToNonISOFormat(effectiveStartDate)
          : undefined;
        const formattedEndDate =
          effectiveEndDate !== null && effectiveEndDate !== undefined
            ? formatDateToNonISOFormat(effectiveEndDate)
            : effectiveEndDate; // Keep null as null

        // Add the effective dates to the data payload
        const dataWithDates = {
          ...data,
          effectiveStartDate: formattedStartDate,
          effectiveEndDate: formattedEndDate,
        };

        handleSaveTeamMember({
          data: dataWithDates,
          successCallback: (patchResult: PatchEntityResult) => {
            setPatchResultToDisplay(patchResult);
            resolve();
          },
          errorCallback: (error: unknown) => reject(error),
        });
      });
    },
    [
      handleSaveTeamMember,
      isEditChangeMode,
      changeToEdit,
      changeId,
      memberId,
      patchChangeMutation,
      setPatchResultToDisplay,
    ]
  );

  const { hasAnyPermission, hasPermission } = usePermissions();
  const { showTimeOffs } = useFeatureFlags();
  const canViewTeamMembers = hasPermission(OpsiumPolicy.TeamMembersViewAll);
  const isDeleteMemberEnabled = hasPermission(OpsiumPolicy.TeamMembersDelete);
  const isAccessGrandAndRevokeEnabled = hasPermission(OpsiumPolicy.AccessGrantAndRevoke);
  const isEditMemberEnabled = hasPermission(OpsiumPolicy.TeamMembersEdit);
  const canManageBilling = hasAnyPermission([
    OpsiumPolicy.ExternalRatesManage,
    OpsiumPolicy.InternalRatesManage,
  ]);
  const canManageCapacity = hasAnyPermission([OpsiumPolicy.TeamMemberCapacityManage]);
  const canViewTimeOff = hasPermission(OpsiumPolicy.TimeOffsViewOthers);
  const canShowTimeOffTab = showTimeOffs && canViewTimeOff;

  // Fallback handler for form state changes when template handler is not yet ready
  const fallbackFormStateChangeHandler = useCallback(
    (_isDirty: boolean, _hasChanges?: boolean, _isValid?: boolean, onlyNonVersioned?: boolean) => {
      setHasOnlyNonVersionedChanges(onlyNonVersioned ?? false);
    },
    []
  );

  const tabs: FeatureTab<TeamMemberFormData>[] = useMemo(() => {
    const tabsArray: FeatureTab<TeamMemberFormData>[] = [];

    tabsArray.push({
      id: TeamMemberTab.Info,
      label: t('authenticated.team:details.tabs.info.title'),
      icon: Info,
      hasWarning: hasInfoTabWarning,
      hasError: !tabValid.info,
      ref: teamMemberInfoFormRef,
      content: wrapWithAlert(
        <TeamMemberInfoTab
          ref={teamMemberInfoFormRef}
          mode={mode}
          featureData={effectiveTeamMemberData}
          draftData={mode === FeatureMode.Create ? currentTabDraftData : undefined}
          onFormStateChange={formStateChangeHandler || fallbackFormStateChangeHandler}
          onDirectSave={handleDirectSave}
        />,
        TeamMemberTab.Info
      ),
    });

    if (canManageBilling) {
      tabsArray.push({
        id: TeamMemberTab.Billing,
        label: t('authenticated.team:details.tabs.billing.title'),
        hasError: !tabValid.billing,
        icon: CircleDollarSign,
        ref: teamMemberBillingFormRef,
        content: wrapWithAlert(
          <TeamMemberBillingTab
            ref={teamMemberBillingFormRef}
            mode={mode}
            featureData={effectiveTeamMemberData}
            draftData={mode === FeatureMode.Create ? currentTabDraftData : undefined}
            onFormStateChange={formStateChangeHandler || fallbackFormStateChangeHandler}
            onDirectSave={handleDirectSave}
            onTabChange={
              mode !== FeatureMode.Create ? wrappedTabChangeHandler || handleTabChange : undefined
            }
          />,
          TeamMemberTab.Billing
        ),
      });
    }

    if (canManageCapacity) {
      tabsArray.push({
        id: TeamMemberTab.Capacity,
        label: t('authenticated.team:details.tabs.capacity.title'),
        hasError: !tabValid.capacity,
        icon: Timer,
        ref: teamMemberCapacityFormRef,
        content: wrapWithAlert(
          <TeamMemberCapacityTab
            ref={teamMemberCapacityFormRef}
            mode={mode}
            featureData={effectiveTeamMemberData}
            draftData={mode === FeatureMode.Create ? currentTabDraftData : undefined}
            onFormStateChange={formStateChangeHandler || fallbackFormStateChangeHandler}
            onDirectSave={handleDirectSave}
          />,
          TeamMemberTab.Capacity
        ),
      });
    }

    if (mode !== FeatureMode.Create && canShowTimeOffTab) {
      // Time-off tab — manager view of another member's balances + requests.
      tabsArray.push({
        id: TeamMemberTab.TimeOff,
        label: t('authenticated.team:details.tabs.timeOff.title'),
        icon: Scale,
        ref: { current: null } as React.RefObject<null>,
        content: wrapWithAlert(
          <TeamMemberTimeOffTab featureData={effectiveTeamMemberData} />,
          TeamMemberTab.TimeOff
        ),
        maxWidth: 'max-w-6xl' as const,
        fillAvailableHeight: true,
      });
    }

    tabsArray.push({
      id: TeamMemberTab.Integrations,
      label: t('authenticated.team:details.tabs.integrations.title'),
      icon: CloudCog,
      hasError: !tabValid.integrations,
      ref: teamMemberIntegrationsFormRef,
      hasWarning: hasIntegrationsTabWarning,
      content: wrapWithAlert(
        <TeamMemberIntegrationsTab
          ref={teamMemberIntegrationsFormRef}
          mode={mode}
          featureData={effectiveTeamMemberData}
          rawIntegrations={
            effectiveTeamMemberData
              ? mapTeamMemberIntegrationsToRawResponse(effectiveTeamMemberData.integrations)
              : undefined
          }
          draftData={currentTabDraftData}
          onFormStateChange={formStateChangeHandler || fallbackFormStateChangeHandler}
        />,
        TeamMemberTab.Integrations
      ),
    });

    if (mode !== FeatureMode.Create) {
      // Only show Changes tab when not in create mode
      tabsArray.push({
        id: TeamMemberTab.Changes,
        label: t('authenticated.team:details.tabs.changes.title'),
        hasError: !tabValid.changes,
        icon: RefreshCw,
        ref: { current: null } as React.RefObject<null>,
        content: wrapWithAlert(
          <TeamMemberChangesTab
            mode={mode}
            featureData={effectiveTeamMemberData}
            onFormStateChange={formStateChangeHandler || fallbackFormStateChangeHandler}
          />,
          TeamMemberTab.Changes
        ),
        maxWidth: 'max-w-6xl' as const,
      });
    }

    return tabsArray;
  }, [
    mode,
    t,
    effectiveTeamMemberData,
    currentTabDraftData,
    formStateChangeHandler,
    fallbackFormStateChangeHandler,
    teamMemberInfoFormRef,
    teamMemberCapacityFormRef,
    teamMemberBillingFormRef,
    teamMemberIntegrationsFormRef,
    wrapWithAlert,
    hasInfoTabWarning,
    hasIntegrationsTabWarning,
    tabValid,
    canManageBilling,
    canManageCapacity,
    canShowTimeOffTab,
    handleDirectSave,
  ]);

  // OPS-1361: redirect to the default tab when the URL tab is permission-gated
  const allowedTabIds = useMemo(() => tabs.map(t => t.id as TeamMemberTab), [tabs]);
  useEffect(() => {
    if (
      mode !== FeatureMode.Create &&
      !isEditChangeMode &&
      allowedTabIds.length > 0 &&
      !allowedTabIds.includes(currentTab)
    ) {
      handleTabChange(TeamMemberTab.Info);
    }
  }, [currentTab, allowedTabIds, mode, isEditChangeMode, handleTabChange]);

  // Custom validation logic for hasAnyData in create mode
  const calculateHasAnyData = useCallback(
    (context: {
      mode: FeatureMode;
      currentTab: string;
      tabs: FeatureTab<TeamMemberFormData>[];
      getDraftData: (tab: string) => Partial<TeamMemberFormData> | undefined;
      hasDraftData: boolean;
    }) => {
      if (context.mode === FeatureMode.Create) {
        // Get draft data for all tabs
        const infoDraft = context.getDraftData(TeamMemberTab.Info);
        const billingDraft = context.getDraftData(TeamMemberTab.Billing);
        const capacityDraft = context.getDraftData(TeamMemberTab.Capacity);

        // Get current tab data if tab has changes but not yet saved to draft
        const infoTab = context.tabs.find(tab => tab.id === TeamMemberTab.Info);
        const billingTab = context.tabs.find(tab => tab.id === TeamMemberTab.Billing);
        const capacityTab = context.tabs.find(tab => tab.id === TeamMemberTab.Capacity);

        const currentInfoData =
          context.currentTab === TeamMemberTab.Info ? infoTab?.ref.current?.getFormData() : null;
        const currentBillingData =
          context.currentTab === TeamMemberTab.Billing
            ? billingTab?.ref.current?.getFormData()
            : null;
        const currentCapacityData =
          context.currentTab === TeamMemberTab.Capacity
            ? capacityTab?.ref.current?.getFormData()
            : null;

        // Merge draft data with current unsaved data
        const finalInfoData = currentInfoData || infoDraft;
        const finalBillingData = currentBillingData || billingDraft;
        const finalCapacityData = currentCapacityData || capacityDraft;

        // Info tab validation (required)
        if (!finalInfoData) {
          return false;
        }

        try {
          // Validate Info tab (required)
          const infoSchema = teamMemberInfoSchema(t);
          const infoResult = infoSchema.safeParse(finalInfoData);
          if (!infoResult.success) {
            return false;
          }

          // Validate Billing tab if it has data (optional - defaults are valid)
          if (finalBillingData && Object.keys(finalBillingData).length > 0) {
            const billingSchema = teamMemberBillingSchema(t);
            const billingResult = billingSchema.safeParse(finalBillingData);
            if (!billingResult.success) {
              return false;
            }
          }

          // Validate Capacity tab if it has data (optional fields but must be valid if present)
          if (finalCapacityData && Object.keys(finalCapacityData).length > 0) {
            const capacitySchema = teamMemberCapacitySchema(t, true);
            const capacityResult = capacitySchema.safeParse(finalCapacityData);
            if (!capacityResult.success) {
              return false;
            }
          }

          return true;
        } catch {
          // If validation fails for any reason, assume invalid
          return false;
        }
      }

      // For edit/read mode, let template handle default logic
      return false;
    },
    [t]
  );

  // Form state change handler
  const [_hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const handleFormStateChange = useCallback((isDirty: boolean) => {
    setHasUnsavedChanges(isDirty);
  }, []);

  // Top right buttons to make actions related to team member (invite/revoke/resend/archive/delete)
  const actionButtons: ActionButtonConfig[] = useMemo(() => {
    if (
      !teamMemberData ||
      teamMemberData.isChampion ||
      mode == FeatureMode.Create ||
      mode == FeatureMode.EditChange
    )
      return [];

    const buttons: ActionButtonConfig[] = [];
    const isNeedsReview = teamMemberData.status === MemberStatus.NeedsReview;

    // Status-specific action buttons (shown before archive/delete)
    if (isAccessGrandAndRevokeEnabled) {
      if (teamMemberData.status === MemberStatus.Basic) {
        buttons.push({
          label: t('authenticated.team:details.actions.invite'),
          icon: UserPlus,
          onClick: handleOpenInviteModal,
          variant: 'outline' as const,
        });
      } else if (teamMemberData.status === MemberStatus.Active) {
        buttons.push({
          label: t('authenticated.team:details.actions.revokeAccess'),
          icon: UserMinus,
          onClick: () => setIsRevokeAccessModalOpen(true),
          variant: 'outline' as const,
        });
      } else if (teamMemberData.status === MemberStatus.Invited) {
        buttons.push({
          label: t('authenticated.team:details.actions.resendInvite'),
          icon: UserPlus,
          onClick: handleOpenResendInviteModal,
          variant: 'outline' as const,
        });
        buttons.push({
          label: t('authenticated.team:details.actions.revokeAccess'),
          icon: UserMinus,
          onClick: () => setIsRevokeAccessModalOpen(true),
          variant: 'outline' as const,
        });
      } else if (teamMemberData.status === MemberStatus.Expired) {
        buttons.push({
          label: t('authenticated.team:details.actions.resendInvite'),
          icon: UserPlus,
          onClick: handleOpenResendInviteModal,
          variant: 'outline' as const,
        });
      }
    }

    // A member that needs review is read-only until resolved in Data reconciliation.
    if (isEditMemberEnabled && mode == FeatureMode.Edit && !isNeedsReview) {
      // Archive/Unarchive button
      buttons.push({
        label:
          teamMemberData.status === MemberStatus.Archived
            ? t('authenticated.team:unarchive.unarchiveTeamMember')
            : t('authenticated.team:archive.archiveTeamMember'),
        icon: teamMemberData.status === MemberStatus.Archived ? ArchiveRestore : ArchiveIcon,
        onClick: () => setIsArchiveModalOpen(true),
        variant: 'outline' as const,
      });
    }

    if (isDeleteMemberEnabled) {
      // Delete button
      buttons.push({
        label: t('authenticated.team:delete.deleteTeamMemberButton'),
        icon: Trash2,
        onClick: () => setIsDeleteModalOpen(true),
        variant: 'outlineDestructive' as const,
      });
    }

    return buttons;
  }, [
    teamMemberData,
    mode,
    t,
    isDeleteMemberEnabled,
    isAccessGrandAndRevokeEnabled,
    isEditMemberEnabled,
    handleOpenInviteModal,
    handleOpenResendInviteModal,
  ]);

  const footerProps = useMemo(
    () => ({
      labels: {
        saving: t('common:loading'),
        create: t('authenticated.team:details.tabs.info.createTeamMember'),
        saveChanges: t('common:save'),
        dateOfChange: t('authenticated.team:details.dateOfChange'),
        selectChangeDate: t('authenticated.team:details.selectChangeDate'),
        updateRecord: t('authenticated.team:details.tabs.changes.updateRecord'),
        cancel: t('common:cancel'),
      },
      // Hide date picker on Integrations tab (non-versioned, never needs conflict detection)
      showDatePicker: currentTab !== TeamMemberTab.Integrations,
      // Disable date picker when only non-versioned fields changed (Info tab only)
      // Billing and Capacity tabs have only versioned fields, so date picker is always enabled
      isDatePickerDisabled: currentTab === TeamMemberTab.Info && hasOnlyNonVersionedChanges,
      datePickerDisabled: (date: Date) => {
        // Disable dates before today (can't make changes in the past)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date < today;
      },
      selectedDate: changeToEdit?.startDate,
      selectedEndDate: changeToEdit?.endDate ?? null,
    }),
    [t, currentTab, changeToEdit?.startDate, changeToEdit?.endDate, hasOnlyNonVersionedChanges]
  );

  const teamMembersForDelete = useMemo(() => {
    if (!teamMemberData) return [];
    return [teamMemberData];
  }, [teamMemberData]);

  const modals = useMemo(
    () => (
      <>
        {/* Invite Modal */}
        {teamMemberData && teamMemberData.status === MemberStatus.Basic && (
          <InviteTeamMemberModal
            teamMember={teamMemberData}
            open={isInviteModalOpen}
            onOpen={handleOpenInviteModal}
            onClose={() => setIsInviteModalOpen(false)}
            onSuccess={() => recordInviteSend(teamMemberData.id)}
          />
        )}

        {/* Resend Invite Modal — available for Invited (alongside revoke) and Expired statuses */}
        {teamMemberData &&
          (teamMemberData.status === MemberStatus.Expired ||
            teamMemberData.status === MemberStatus.Invited) && (
            <ResendInviteTeamMemberModal
              teamMember={teamMemberData}
              open={isResendInviteModalOpen}
              onOpen={handleOpenResendInviteModal}
              onClose={() => setIsResendInviteModalOpen(false)}
              onSuccess={() => recordInviteSend(teamMemberData.id)}
            />
          )}

        {/* Invite cooldown info modal — shown when a (re)send would violate Cognito's 1h limit */}
        <InviteCooldownModal
          open={isInviteCooldownModalOpen}
          onOpen={() => setIsInviteCooldownModalOpen(true)}
          onClose={() => setIsInviteCooldownModalOpen(false)}
          remainingMs={remainingMs}
        />

        {/* Revoke Access Modal */}
        {teamMemberData &&
          (teamMemberData.status === MemberStatus.Active ||
            teamMemberData.status === MemberStatus.Invited) && (
            <RevokeAccessTeamMemberModal
              teamMember={teamMemberData}
              open={isRevokeAccessModalOpen}
              onOpen={() => setIsRevokeAccessModalOpen(true)}
              onClose={() => setIsRevokeAccessModalOpen(false)}
            />
          )}

        {/* Archive/Unarchive Modal - Externally controlled */}
        {teamMemberData &&
          (teamMemberData.status === MemberStatus.Archived ? (
            <UnarchiveTeamMemberModal
              teamMember={teamMemberData}
              open={isArchiveModalOpen}
              onOpen={() => setIsArchiveModalOpen(true)}
              onClose={() => setIsArchiveModalOpen(false)}
            />
          ) : (
            <ArchiveTeamMemberModal
              teamMember={teamMemberData}
              open={isArchiveModalOpen}
              onOpen={() => setIsArchiveModalOpen(true)}
              onClose={() => setIsArchiveModalOpen(false)}
            />
          ))}

        {/* Delete Modal - Externally controlled */}
        {teamMemberData && (
          <DeleteTeamMembersModal
            teamMembersForDelete={teamMembersForDelete}
            onSuccess={() => navigate(AuthenticatedRoutes.Team)}
            open={isDeleteModalOpen}
            onOpen={() => setIsDeleteModalOpen(true)}
            onClose={() => setIsDeleteModalOpen(false)}
          />
        )}
      </>
    ),
    [
      mode,
      teamMemberData,
      isInviteModalOpen,
      isResendInviteModalOpen,
      isRevokeAccessModalOpen,
      isArchiveModalOpen,
      isDeleteModalOpen,
      isInviteCooldownModalOpen,
      remainingMs,
      handleOpenInviteModal,
      handleOpenResendInviteModal,
      recordInviteSend,
      teamMembersForDelete,
      navigate,
    ]
  );

  // Combined loading states for EditChange mode
  const combinedLoadingEntity = isLoadingTeamMember || isLoadingHistorical;
  const combinedIsLoading = isLoading || patchChangeMutation.isPending;

  // Guard access — redirect to forbidden when View team members is disabled
  if (memberId !== 'new' && !canViewTeamMembers) {
    return <Navigate to={AuthenticatedRoutes.Forbidden} replace />;
  }

  return (
    <FeatureDetailsTemplate<TeamMember, TeamMemberFormData>
      entityData={effectiveTeamMemberData}
      mode={mode as unknown as FeatureMode}
      currentTab={currentTab}
      isLoading={combinedIsLoading}
      isLoadingEntity={combinedLoadingEntity}
      error={error}
      tabs={tabs}
      getDraftData={(tab: string) => getDraftData(tab as TeamMemberTab)}
      saveDraftData={({ tab, data }) => saveDraftData({ tab: tab as TeamMemberTab, data })}
      clearDraft={clearDraft}
      getFinalSubmissionData={getFinalSubmissionData}
      hasDraftData={hasDraftData}
      setTabValid={handleSetTabValid}
      calculateHasAnyData={context => {
        const result = calculateHasAnyData({
          mode: context.mode,
          currentTab: context.currentTab,
          tabs: context.tabs,
          getDraftData: context.getDraftData,
          hasDraftData: context.hasDraftData,
        });
        return result ?? false;
      }}
      handleSave={handleSaveTeamMember}
      // Actions
      onBack={() => navigate(AuthenticatedRoutes.Team)}
      onFormStateChange={handleFormStateChange}
      onTabChange={(tab: string, additionalParams?: Record<string, string>) => {
        setHasOnlyNonVersionedChanges(false);
        handleTabChange(tab, additionalParams);
      }}
      onCancelEditChange={isEditChangeMode ? handleCancelEditChange : undefined}
      // Unsaved changes confirmation
      enableUnsavedChangesConfirmation={
        mode === FeatureMode.Edit || mode === FeatureMode.Create || mode === FeatureMode.EditChange
      }
      unsavedChangesConfig={{
        title: t(
          mode === FeatureMode.Create
            ? 'authenticated.team:details.confirmations.discardCreation.title'
            : 'authenticated.team:details.confirmations.discardChanges.title'
        ),
        message: t(
          mode === FeatureMode.Create
            ? 'authenticated.team:details.confirmations.discardCreation.message'
            : 'authenticated.team:details.confirmations.discardChanges.message'
        ),
        confirmText: t(
          mode === FeatureMode.Create
            ? 'authenticated.team:details.confirmations.discardCreation.confirm'
            : 'authenticated.team:details.confirmations.discardChanges.confirm'
        ),
        confirmVariant: 'destructive',
      }}
      onFormStateChangeHandlerReady={handleFormStateChangeHandlerReady}
      onTabChangeHandlerReady={handleTabChangeHandlerReady}
      onAllowNavigationReady={handleAllowNavigationReady}
      onRefetch={handleRefetch}
      // Customization
      pageTitle={t('authenticated.team:details.teamMemberDetails')}
      createTitle={t('authenticated.team:details.tabs.info.createTeamMember')}
      loadingMessage={t('authenticated.team:details.loadingTeamMember')}
      errorMessage={t('common:error.reloadPage')}
      showAvatar={true}
      showStatus={true}
      statusBadgeType={StatusBadgeType.TeamMember}
      avatarEditable={true}
      onAvatarChange={handleAvatarUpload}
      onAvatarRemove={handleAvatarRemove}
      isUploadingAvatar={isUploadingAvatar}
      getStatusText={getMemberStatusText}
      actionButtons={isEditChangeMode ? [] : actionButtons}
      forceActionButtonsDropdown={teamMemberData?.status === MemberStatus.Invited}
      useGenericFooter={true}
      footerProps={footerProps}
      disabledTabs={disabledTabs}
      hideFooter={
        mode === FeatureMode.Read ||
        effectiveTeamMemberData?.status === MemberStatus.Archived ||
        effectiveTeamMemberData?.status === MemberStatus.NeedsReview ||
        (currentTab === TeamMemberTab.Changes && !isEditChangeMode) ||
        currentTab === TeamMemberTab.TimeOff
      }
      modals={modals}
      errorActions={[
        {
          label: t('common:backToTeam'),
          onClick: () => navigate(AuthenticatedRoutes.Team),
          variant: 'secondary',
        },
      ]}
      patchResultToDisplay={patchResultToDisplay}
    />
  );
}
